import { rejectInventoryPracticeWrite } from '../../components/onboarding/practice/inventoryPracticeData';
import { getAccessToken } from '../authentication/supabaseService';

export type InventoryGroupType = 'food' | 'drinks';

export interface InventoryGroup {
  id: string;
  restaurantId: string;
  name: string;
  groupType: InventoryGroupType;
  position: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface InventorySection {
  id: string;
  restaurantId: string;
  groupId: string;
  sectionName: string;
  position: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface InventoryCategory {
  id: string;
  restaurantId: string;
  name: string;
  color: string | null;
  position: number;
  createdAt: Date;
  updatedAt: Date;
}

export type StdUnit = 'g' | 'ml' | 'p' | 'unit';

/**
 * Where a section product comes from. `order` = an order-history product, `custom` = a
 * custom ingredient (`custom:<id>`), `subrecipe` = a prep counted in its portion unit
 * (`subrecipe:<id>`, priced from the prep's own ingredients).
 */
export type InventoryProductSource = 'order' | 'custom' | 'subrecipe';

export interface AdditionalCountUnit {
  unit: string;
  factor: number;
}

export interface InventorySectionProduct {
  id: string;
  inventorySectionId: string;
  restaurantId: string;
  productKey: string;
  productName: string;
  position: number;
  createdAt: Date;
  stdUnit: StdUnit;
  countUnit: string;
  countUnitToStdFactor: number;
  additionalCountUnits: AdditionalCountUnit[];
  source: InventoryProductSource;
  customIngredientId?: string | null;
  subrecipeId?: string | null;
  inventoryCategoryId?: string | null;
  inventoryCategoryName?: string | null;
  inventoryCategoryPosition?: number | null;
  pricePerStdUnit?: number | null;
  pricePerCountUnit?: number | null;
  weightString?: string;
  latestPurchaseDate?: string | null;
  /** Supplier of the latest order line (or the custom ingredient's supplier). Null when the product left order history. */
  platform?: string | null;
  /** Minimum stock ("Standaard voorraad") in `minStockCountUnit`. Restaurant-wide across sections; null when none is set. */
  minStockCount?: number | null;
  minStockCountUnit?: string | null;
}

export interface AvailableProduct {
  productKey: string;
  productName: string;
  latestUnitPrice: number;
  pricePerStdUnit: number | null;
  stdUnit: StdUnit;
  weightString?: string;
  platform: string;
  category?: string;
  mainCategory?: string;
  latestPurchaseDate: string;
  last60Qty?: number;
  last60Unit?: string;
  unitPrice?: number;
  unitPriceUnit?: string;
  productPrice?: number;   // ex-VAT buy price per single unit (latestUnitPrice / stdMultiplier)
  assignedToSectionIds: string[];
  assignedToSectionNames: string[];
}

export interface AvailableCustomIngredient {
  productKey: string;            // 'custom:<id>'
  customIngredientId: string;
  productName: string;
  latestUnitPrice: number;
  pricePerStdUnit: number | null;
  stdUnit: StdUnit;
  platform: string;
  assignedToSectionIds: string[];
  assignedToSectionNames: string[];
}

/**
 * One row as `/available-subrecipes` puts it on the wire. The endpoint ships the same
 * `productKey` / `subrecipeId` / `productName` trio as its two sibling lists; `id` / `name`
 * are accepted as well because the feature was specced with the subrecipe naming itself that
 * way, and either spelling identifies the same prep. Every identity field is therefore
 * optional here and reconciled in `parseAvailableSubrecipe` — the type says what the parser
 * really tolerates rather than pinning one of the two spellings.
 */
interface AvailableSubrecipeResponse {
  subrecipeId?: string;
  id?: string;
  productName?: string;
  name?: string;
  /** Omissible: fully derivable from the subrecipe id. */
  productKey?: string;
  portions: number;
  portionsUnit: string;
  stdUnit: StdUnit;
  pricePerStdUnit: number | null;
  assignedToSectionIds?: string[];
  assignedToSectionNames?: string[];
}

/**
 * A subrecipe (prep) that can be assigned to a section and counted in its portion unit.
 * Priced at the same €/portion-unit the subrecipe page shows — derived from the prep's own
 * ingredients, so there is no supplier and no order line behind it. Normalized at the
 * service boundary onto the same `productKey`/`productName` shape as the other two picker
 * lists, so every consumer treats the three sources alike.
 */
export interface AvailableSubrecipe {
  productKey: string;            // 'subrecipe:<id>'
  subrecipeId: string;
  productName: string;
  /** The full yield the prep's ingredient quantities are expressed for. */
  portions: number;
  /** The yield unit as stored on the subrecipe ('p' | 'g' | 'ml'). */
  portionsUnit: string;
  stdUnit: StdUnit;
  pricePerStdUnit: number | null;
  assignedToSectionIds: string[];
  assignedToSectionNames: string[];
}

function parseAvailableSubrecipe(raw: AvailableSubrecipeResponse): AvailableSubrecipe {
  const subrecipeId = raw.subrecipeId ?? raw.id ?? '';
  return {
    productKey: raw.productKey || `subrecipe:${subrecipeId}`,
    subrecipeId,
    productName: raw.productName ?? raw.name ?? '',
    portions: raw.portions,
    portionsUnit: raw.portionsUnit,
    stdUnit: raw.stdUnit,
    pricePerStdUnit: raw.pricePerStdUnit,
    // The picker indexes these two in lockstep — never leave one undefined.
    assignedToSectionIds: raw.assignedToSectionIds ?? [],
    assignedToSectionNames: raw.assignedToSectionNames ?? [],
  };
}

/**
 * Normalized display unit price — kg/L for g/ml, raw for pieces, fallback to per-unit.
 * Matches the order history products page convention. `latestUnitPrice` is optional so
 * subrecipes (no order line, never a `unit` std unit) can be priced through the same call.
 */
export function formatNormalizedUnitPrice(
  p: { stdUnit: StdUnit; pricePerStdUnit: number | null; latestUnitPrice?: number }
): { value: number; unit: string } | null {
  if (p.stdUnit === 'g' && p.pricePerStdUnit != null) return { value: p.pricePerStdUnit * 1000, unit: 'kg' };
  if (p.stdUnit === 'ml' && p.pricePerStdUnit != null) return { value: p.pricePerStdUnit * 1000, unit: 'L' };
  if (p.stdUnit === 'p' && p.pricePerStdUnit != null) return { value: p.pricePerStdUnit, unit: 'p' };
  if (p.stdUnit === 'unit' && p.latestUnitPrice) return { value: p.latestUnitPrice, unit: 'unit' };
  return null;
}

export interface InventoryCountItem {
  productKey: string;
  productName: string;
  count: number;
  countUnit: string;
  stdUnit: string;
  countInStdUnit: number;
  pricePerStdUnit: number;
  value: number;
}

export interface InventoryCount {
  id: string;
  restaurantId: string;
  inventorySectionId: string | null;
  sectionNameSnapshot: string;
  countedAt: Date;
  countedByUserId: string | null;
  totalValue: number;
  items: InventoryCountItem[];
  sessionId: string | null;
  groupId: string | null;
  groupNameSnapshot: string | null;
}

export interface CurrentValueResponse {
  totalValue: number;
  perSection: {
    sectionId: string | null;
    sectionName: string;
    lastCountedAt: string;
    totalValue: number;
    groupId: string | null;
    groupName: string | null;
  }[];
}

export interface AssignProductsResult {
  inserted: InventorySectionProduct[];
  skipped: {
    productKey: string;
    productName: string;
    existingSectionId: string;
    existingSectionName: string;
  }[];
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function parseSection(raw: any): InventorySection {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

function parseGroup(raw: any): InventoryGroup {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

/** Error thrown when deleting a group that still has sections (server 409). */
export class GroupNotEmptyError extends Error {
  constructor() {
    super('group_not_empty');
    this.name = 'GroupNotEmptyError';
  }
}

// ---------- Groups ----------

export async function fetchGroupsByRestaurant(restaurantId: string): Promise<InventoryGroup[]> {
  const res = await fetch(`/api/inventory/groups/${restaurantId}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch inventory groups: ${res.status}`);
  const data = await res.json();
  return data.map(parseGroup);
}

export async function createGroup(payload: { restaurantId: string; name: string; groupType: InventoryGroupType; position?: number }): Promise<InventoryGroup> {
  rejectInventoryPracticeWrite(payload);
  const res = await fetch('/api/inventory/groups', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to create inventory group: ${res.status}`);
  return parseGroup(await res.json());
}

export async function updateGroup(id: string, payload: { name?: string; groupType?: InventoryGroupType; position?: number }): Promise<InventoryGroup> {
  rejectInventoryPracticeWrite(id, payload);
  const res = await fetch(`/api/inventory/groups/${id}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to update inventory group: ${res.status}`);
  return parseGroup(await res.json());
}

export async function deleteGroup(id: string): Promise<void> {
  rejectInventoryPracticeWrite(id);
  const res = await fetch(`/api/inventory/groups/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (res.status === 409) throw new GroupNotEmptyError();
  if (!res.ok) throw new Error(`Failed to delete inventory group: ${res.status}`);
}

function parseCategory(raw: any): InventoryCategory {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

/** Error thrown when a category name already exists for the restaurant (server 409). */
export class CategoryNameExistsError extends Error {
  constructor() {
    super('category_name_exists');
    this.name = 'CategoryNameExistsError';
  }
}

// ---------- Categories ----------

export async function fetchCategoriesByRestaurant(restaurantId: string): Promise<InventoryCategory[]> {
  const res = await fetch(`/api/inventory/categories/${restaurantId}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch inventory categories: ${res.status}`);
  const data = await res.json();
  return data.map(parseCategory);
}

export async function createCategory(payload: { restaurantId: string; name: string; color?: string; position?: number }): Promise<InventoryCategory> {
  rejectInventoryPracticeWrite(payload);
  const res = await fetch('/api/inventory/categories', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (res.status === 409) throw new CategoryNameExistsError();
  if (!res.ok) throw new Error(`Failed to create inventory category: ${res.status}`);
  return parseCategory(await res.json());
}

export async function updateCategory(id: string, payload: { name?: string; color?: string; position?: number }): Promise<InventoryCategory> {
  rejectInventoryPracticeWrite(id, payload);
  const res = await fetch(`/api/inventory/categories/${id}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (res.status === 409) throw new CategoryNameExistsError();
  if (!res.ok) throw new Error(`Failed to update inventory category: ${res.status}`);
  return parseCategory(await res.json());
}

export async function deleteCategory(id: string): Promise<void> {
  rejectInventoryPracticeWrite(id);
  const res = await fetch(`/api/inventory/categories/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to delete inventory category: ${res.status}`);
}

export async function updateCategoryPositions(updates: { id: string; position: number }[]): Promise<void> {
  rejectInventoryPracticeWrite(updates);
  const res = await fetch('/api/inventory/categories/positions', {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) throw new Error(`Failed to update category positions: ${res.status}`);
}

function parseProduct(raw: any): InventorySectionProduct {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    additionalCountUnits: Array.isArray(raw.additionalCountUnits) ? raw.additionalCountUnits : [],
  };
}

function parseCount(raw: any): InventoryCount {
  return {
    ...raw,
    countedAt: new Date(raw.countedAt),
    items: Array.isArray(raw.items) ? raw.items : [],
  };
}

// ---------- Sections ----------

export async function fetchSectionsByRestaurant(restaurantId: string): Promise<InventorySection[]> {
  const res = await fetch(`/api/inventory/sections/${restaurantId}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch inventory sections: ${res.status}`);
  const data = await res.json();
  return data.map(parseSection);
}

export async function createSection(payload: { restaurantId: string; groupId: string; sectionName: string; position?: number }): Promise<InventorySection> {
  rejectInventoryPracticeWrite(payload);
  const res = await fetch('/api/inventory/sections', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to create inventory section: ${res.status}`);
  return parseSection(await res.json());
}

export async function updateSection(id: string, payload: { sectionName?: string; position?: number; groupId?: string }): Promise<InventorySection> {
  rejectInventoryPracticeWrite(id, payload);
  const res = await fetch(`/api/inventory/sections/${id}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to update inventory section: ${res.status}`);
  return parseSection(await res.json());
}

export async function deleteSection(id: string): Promise<void> {
  rejectInventoryPracticeWrite(id);
  const res = await fetch(`/api/inventory/sections/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to delete inventory section: ${res.status}`);
}

// ---------- Section products ----------

export async function fetchProductsBySection(sectionId: string): Promise<InventorySectionProduct[]> {
  const res = await fetch(`/api/inventory/sections/${sectionId}/products`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch section products: ${res.status}`);
  const data = await res.json();
  return data.map(parseProduct);
}

/**
 * One product's CANONICAL unit config inside an inventory group — the group's first
 * assignment by (section position, product position), i.e. the unit config a counter sees.
 * `productKey` is the RAW key: variant rows (`…__v<n>`) come back separately and any report
 * lookup must fold them with `baseProductKey`.
 */
export interface GroupProductUnit {
  productKey: string;
  stdUnit: StdUnit;
  countUnit: string;
  countUnitToStdFactor: number;
}

/**
 * The group's deduplicated product list with its canonical units. Read-only, auth-only, and
 * shared with the Cloud Count app's "From other sections" list — only the unit fields are
 * read here. Fetched lazily: nothing on the web needs it until the leakage tab is switched
 * into count units.
 */
export async function fetchGroupProductUnits(groupId: string, restaurantId: string): Promise<GroupProductUnit[]> {
  const res = await fetch(`/api/inventory/groups/${groupId}/all-products/${restaurantId}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch group products: ${res.status}`);
  const data = await res.json();
  return (data.products ?? []).map((p: Record<string, unknown>) => ({
    productKey: String(p.productKey ?? ''),
    stdUnit: p.stdUnit as StdUnit,
    countUnit: String(p.countUnit ?? ''),
    countUnitToStdFactor: Number(p.countUnitToStdFactor ?? 0),
  }));
}

/**
 * Assign products to `sectionId`, or — with `extraSectionIds` — to that section AND the listed
 * ones in a single request: the server builds its price maps once and writes all sections in one
 * transaction. `inserted` rows carry their own `inventorySectionId`, `skipped` ones the section
 * they were already in, so the caller can split a fan-out result per section.
 */
export async function assignProductsToSection(
  sectionId: string,
  products: { productKey: string; productName: string; source?: InventoryProductSource; customIngredientId?: string; subrecipeId?: string; inventoryCategoryId?: string | null; asVariant?: boolean }[],
  extraSectionIds?: string[]
): Promise<AssignProductsResult> {
  rejectInventoryPracticeWrite(sectionId, products, extraSectionIds);
  const res = await fetch(`/api/inventory/sections/${sectionId}/products`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(
      extraSectionIds && extraSectionIds.length > 0 ? { products, sectionIds: extraSectionIds } : { products }
    ),
  });
  if (!res.ok) throw new Error(`Failed to assign products: ${res.status}`);
  const data = await res.json();
  return {
    inserted: data.inserted.map(parseProduct),
    skipped: data.skipped,
  };
}

export async function updateSectionProductUnits(
  productId: string,
  payload: { countUnit: string; countUnitToStdFactor: number; additionalCountUnits: AdditionalCountUnit[] }
): Promise<InventorySectionProduct> {
  rejectInventoryPracticeWrite(productId, payload);
  const res = await fetch(`/api/inventory/sections/products/${productId}/units`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to update product units: ${res.status}`);
  return parseProduct(await res.json());
}

/** Error thrown when a sibling row of the same product in the section already uses the name (server 409). */
export class ProductNameExistsError extends Error {
  constructor() {
    super('product_name_exists');
    this.name = 'ProductNameExistsError';
  }
}

/**
 * Rename a section product's per-assignment display name. Past saved counts keep
 * the name they were counted under (same convention as unit edits). Sibling rows
 * of the same base product must keep distinct names → 409 `ProductNameExistsError`.
 */
export async function renameSectionProduct(
  productId: string,
  payload: { sectionId: string; productName: string }
): Promise<InventorySectionProduct> {
  rejectInventoryPracticeWrite(productId, payload);
  const res = await fetch(`/api/inventory/sections/products/${productId}/name`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (res.status === 409) throw new ProductNameExistsError();
  if (!res.ok) throw new Error(`Failed to rename section product: ${res.status}`);
  return parseProduct(await res.json());
}

/**
 * Re-point a section product at a different order-history product, custom ingredient or
 * subrecipe. The server re-resolves units/price from the target; `position` +
 * `inventory_category_id` are preserved. Throws with the server's error code
 * (`already_in_section`) when the target is already assigned to the same section, or
 * `not_found` when the target key is unknown.
 */
export async function relinkSectionProduct(
  productId: string,
  payload: { productKey: string; source: InventoryProductSource; customIngredientId?: string; subrecipeId?: string }
): Promise<InventorySectionProduct> {
  rejectInventoryPracticeWrite(productId, payload);
  const res = await fetch(`/api/inventory/sections/products/${productId}/link`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let code = '';
    try {
      code = (await res.json())?.error ?? '';
    } catch {
      /* body not JSON — fall back to the status message */
    }
    throw new Error(code || `Failed to relink section product: ${res.status}`);
  }
  return parseProduct(await res.json());
}

/**
 * Re-link a counted product by its frozen key instead of its assignment id — used for
 * "Not linked" rows whose section assignment no longer exists (removed from the section,
 * or re-linked elsewhere after the count was saved). The server re-keys the section's
 * saved counts and re-creates the assignment on the target when it is missing.
 */
export async function relinkSectionProductByKey(
  sectionId: string,
  payload: {
    oldProductKey: string;
    productKey: string;
    source: InventoryProductSource;
    customIngredientId?: string;
    subrecipeId?: string;
    inventoryCategoryId?: string | null;
  }
): Promise<InventorySectionProduct | null> {
  rejectInventoryPracticeWrite(sectionId, payload);
  const res = await fetch(`/api/inventory/sections/${sectionId}/products/link-by-key`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let code = '';
    try {
      code = (await res.json())?.error ?? '';
    } catch {
      /* body not JSON — fall back to the status message */
    }
    throw new Error(code || `Failed to relink section product: ${res.status}`);
  }
  const body = await res.json();
  return body ? parseProduct(body) : null;
}

export async function setProductCategory(
  productId: string,
  inventoryCategoryId: string | null
): Promise<InventorySectionProduct> {
  rejectInventoryPracticeWrite(productId, inventoryCategoryId);
  const res = await fetch(`/api/inventory/sections/products/${productId}/category`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ inventoryCategoryId }),
  });
  if (!res.ok) throw new Error(`Failed to update product category: ${res.status}`);
  return parseProduct(await res.json());
}

export async function updateSectionProductPositions(
  sectionId: string,
  positions: { id: string; position: number }[]
): Promise<void> {
  rejectInventoryPracticeWrite(sectionId, positions);
  const res = await fetch(`/api/inventory/sections/${sectionId}/products/positions`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ positions }),
  });
  if (!res.ok) throw new Error(`Failed to update product positions: ${res.status}`);
}

export async function removeProductFromSection(productId: string): Promise<void> {
  rejectInventoryPracticeWrite(productId);
  const res = await fetch(`/api/inventory/sections/products/${productId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to remove product: ${res.status}`);
}

// ---------- Available products ----------

export async function fetchAvailableProducts(restaurantId: string): Promise<AvailableProduct[]> {
  const res = await fetch(`/api/inventory/available-products/${restaurantId}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch available products: ${res.status}`);
  return res.json();
}

export async function fetchAvailableCustomIngredients(restaurantId: string): Promise<AvailableCustomIngredient[]> {
  const res = await fetch(`/api/inventory/available-custom-ingredients/${restaurantId}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch available custom ingredients: ${res.status}`);
  return res.json();
}

export async function fetchAvailableSubrecipes(restaurantId: string): Promise<AvailableSubrecipe[]> {
  const res = await fetch(`/api/inventory/available-subrecipes/${restaurantId}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch available subrecipes: ${res.status}`);
  const data = await res.json();
  return (data as AvailableSubrecipeResponse[]).map(parseAvailableSubrecipe);
}

// ---------- Counts ----------

export async function fetchCountsByRestaurant(restaurantId: string, options: { limit?: number; beforeDate?: string; complete?: boolean } = {}): Promise<InventoryCount[]> {
  const params = new URLSearchParams();
  if (options.complete) params.set('complete', 'true');
  if (options.limit) params.set('limit', String(options.limit));
  if (options.beforeDate) params.set('beforeDate', options.beforeDate);
  const qs = params.toString();
  const res = await fetch(`/api/inventory/counts/${restaurantId}${qs ? `?${qs}` : ''}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch inventory counts: ${res.status}`);
  const data = await res.json();
  return data.map(parseCount);
}

export async function fetchCountById(id: string): Promise<InventoryCount> {
  const res = await fetch(`/api/inventory/counts/by-id/${id}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch inventory count: ${res.status}`);
  return parseCount(await res.json());
}

export async function createCount(payload: {
  restaurantId: string;
  inventorySectionId: string;
  items: { productKey: string; count: number }[];
}): Promise<InventoryCount> {
  rejectInventoryPracticeWrite(payload);
  const res = await fetch('/api/inventory/counts', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to save inventory count: ${res.status}`);
  return parseCount(await res.json());
}

export async function deleteCounts(restaurantId: string, ids: string[]): Promise<number> {
  rejectInventoryPracticeWrite(restaurantId, ids);
  const res = await fetch('/api/inventory/counts/delete', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, ids }),
  });
  if (!res.ok) throw new Error(`Failed to delete inventory session: ${res.status}`);
  const data = await res.json();
  return data.deleted as number;
}

/**
 * Correct the counted quantities of a saved count (History tab "Edit count").
 * Quantities only — the server keeps each item in the unit it was counted in and its frozen
 * price, and recomputes countInStdUnit / value / total_value from the new numbers.
 */
export async function updateCountQuantities(
  countId: string,
  restaurantId: string,
  items: { productKey: string; count: number }[]
): Promise<InventoryCount> {
  rejectInventoryPracticeWrite(countId, restaurantId, items);
  const res = await fetch(`/api/inventory/counts/${countId}/quantities`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, items }),
  });
  if (!res.ok) throw new Error(`Failed to update the counted quantities: ${res.status}`);
  const data = await res.json();
  return parseCount(data.count);
}

/**
 * Add product lines to a saved count (section page "Add product count"). The server prices
 * them through the same block as a fresh save, so an added line carries TODAY's live price —
 * a product that was never counted here has no historical frozen price to inherit.
 */
export async function addCountItems(
  countId: string,
  restaurantId: string,
  items: { productKey: string; count: number; countUnit?: string }[]
): Promise<InventoryCount> {
  rejectInventoryPracticeWrite(countId, restaurantId, items);
  const res = await fetch(`/api/inventory/counts/${countId}/items`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, items }),
  });
  if (!res.ok) throw new Error(`Failed to add products to the count: ${res.status}`);
  const data = await res.json();
  return parseCount(data.count);
}

/**
 * Correct when saved counts were taken (History tab "Edit date"). One timestamp per count so
 * each keeps its own time-of-day; the date itself is resolved in the browser's timezone, the
 * same one it is displayed in.
 */
export async function updateCountsDate(
  restaurantId: string,
  items: { id: string; countedAt: Date }[]
): Promise<InventoryCount[]> {
  rejectInventoryPracticeWrite(restaurantId, items);
  const res = await fetch('/api/inventory/counts/date', {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({
      restaurantId,
      items: items.map((i) => ({ id: i.id, countedAt: i.countedAt.toISOString() })),
    }),
  });
  if (!res.ok) throw new Error(`Failed to update the inventory count date: ${res.status}`);
  const data = await res.json();
  return (data.counts as unknown[]).map(parseCount);
}

/** Remove one product line from a saved count (counted by mistake). */
export async function deleteCountItem(
  countId: string,
  restaurantId: string,
  productKey: string
): Promise<InventoryCount> {
  rejectInventoryPracticeWrite(countId, restaurantId, productKey);
  const res = await fetch(`/api/inventory/counts/${countId}/items/delete`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, productKey }),
  });
  if (!res.ok) throw new Error(`Failed to remove the product from the count: ${res.status}`);
  const data = await res.json();
  return parseCount(data.count);
}

/**
 * Rename one product line inside a saved count (section page inline name edit). Display name
 * only — quantity, unit, frozen price and total value stay exactly as counted. Scoped to this
 * count; future counts follow the section product's name (renamed on /inventory/edit).
 */
export async function renameCountItem(
  countId: string,
  restaurantId: string,
  productKey: string,
  productName: string
): Promise<InventoryCount> {
  rejectInventoryPracticeWrite(countId, restaurantId, productKey, productName);
  const res = await fetch(`/api/inventory/counts/${countId}/items/rename`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, productKey, productName }),
  });
  if (!res.ok) throw new Error(`Failed to rename the product in the count: ${res.status}`);
  const data = await res.json();
  return parseCount(data.count);
}

export async function fetchCurrentValue(restaurantId: string): Promise<CurrentValueResponse> {
  const res = await fetch(`/api/inventory/current-value/${restaurantId}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch current inventory value: ${res.status}`);
  return res.json();
}

// ---------- Sessions (count selector) ----------

/** One saved count session (single-group by design). Mirrors the server aggregation. */
export interface InventorySessionSummary {
  sessionId: string | null;
  completedAt: Date;
  sectionCount: number;
  totalValue: number;
  countIds: string[];
  groupId: string | null;
  groupName: string | null;
  /** Counted product lines across the session's counts. Optional: older servers omit it. */
  productCount?: number;
  /** Who finalized the session. Optional: older servers omit it. */
  countedByUserId?: string | null;
  /** Display name of `countedByUserId`. Optional: older servers omit it. */
  countedByName?: string | null;
}

function parseSession(raw: any): InventorySessionSummary {
  return { ...raw, completedAt: new Date(raw.completedAt) };
}

/**
 * Fetch saved count sessions newest-first for the count selector. Keyset-paginated
 * via `beforeDate` (pass the last row's `completedAt.toISOString()` for the next page)
 * and optionally scoped to one inventory group.
 */
export async function fetchSessions(
  restaurantId: string,
  options: { limit?: number; beforeDate?: string; groupId?: string } = {}
): Promise<InventorySessionSummary[]> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.beforeDate) params.set('beforeDate', options.beforeDate);
  if (options.groupId) params.set('groupId', options.groupId);
  const qs = params.toString();
  const res = await fetch(`/api/inventory/sessions/${restaurantId}${qs ? `?${qs}` : ''}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch inventory sessions: ${res.status}`);
  const data = await res.json();
  return data.map(parseSession);
}

// ---------- All-restaurants overview (Inventory page, "All Restaurants" mode) ----------

/**
 * One count session across EVERY restaurant the user can access. Same aggregation as
 * {@link InventorySessionSummary} plus `restaurantId`, minus `productCount` (the server
 * drops it — detoasting the items jsonb per row is the only expensive part of the query).
 * The restaurant set is resolved server-side from the token; the client never sends ids.
 */
export interface InventoryOverviewSessionRow {
  restaurantId: string;
  sessionId: string | null;
  completedAt: Date;
  sectionCount: number;
  totalValue: number;
  countIds: string[];
  groupId: string | null;
  groupName: string | null;
  countedByUserId: string | null;
  countedByName: string | null;
}

export interface InventoryOverviewResponse {
  rows: InventoryOverviewSessionRow[];
  hasMore: boolean;
}

/**
 * Fetch the all-restaurants count overview.
 * - `latest`: one row per (restaurant, group) — the most recent session. Never paginated.
 * - `feed`: every session newest-first, keyset-paginated on `completedAt` (pass the last
 *   row's `completedAt.toISOString()` as `beforeDate` for the next page).
 */
export async function fetchInventoryOverview(options: {
  mode: 'latest' | 'feed';
  limit?: number;
  beforeDate?: string;
}): Promise<InventoryOverviewResponse> {
  const params = new URLSearchParams({ mode: options.mode });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.beforeDate) params.set('beforeDate', options.beforeDate);
  const res = await fetch(`/api/inventory/overview?${params.toString()}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch the inventory overview: ${res.status}`);
  const data = await res.json();
  return {
    rows: (data.rows as any[]).map((raw) => ({ ...raw, completedAt: new Date(raw.completedAt) })),
    hasMore: Boolean(data.hasMore),
  };
}

// ---------- Count Review (single aggregation endpoint for the Count Review UI) ----------
//
// Types mirror the server payload in `server/domains/inventoryReview/countReviewBuilder.ts`
// (`CountReviewPayload`). Frozen values (`value`, `pricePerStdUnit`) come straight from the
// saved count; live order data supplies only supplementary columns + classification.

export interface CountReviewProduct {
  productKey: string;
  productName: string;
  /** The inventory_counts row this item was frozen into (targets the set-price fix). */
  countId: string;
  sectionId: string | null;
  /** The section-product assignment row id (for the P4 re-link modal); null if the product left the section. */
  inventorySectionProductId: string | null;
  sectionName: string;
  groupId: string | null;
  groupName: string | null;
  inventoryCategoryId: string | null;
  inventoryCategoryName: string | null;
  productCategory: string | null;
  mainCategory: string | null;
  subCategories: string[];
  count: number;
  countUnit: string;
  stdUnit: string;
  countInStdUnit: number;
  quantityDisplay: { value: number; unit: string };
  pricePerStdUnit: number;
  unitPrice: number | null;
  stdUnitPrice: { value: number; unit: string } | null;
  value: number;
  supplier: { platform: string | null; name: string | null };
  source: InventoryProductSource;
  linked: boolean;
  missingPrice: boolean;
  belowMinimum: boolean;
  /** True only for `configMissingPrice` / `configNotLinked` rows — configured but never counted. */
  neverCounted: boolean;
}

/**
 * One assignment of a product into a section. Re-linking is per section (a product can sit
 * in many), so a deduped row has to carry every assignment it stands for.
 * Mirrors the server's `CountReviewAssignment`.
 */
export interface CountReviewAssignment {
  inventorySectionProductId: string;
  sectionId: string;
  sectionName: string;
  inventoryCategoryId: string | null;
  /** This assignment's own key — sections can hold different `__v<n>` variants of one base
   *  key, and the by-key re-link path resolves on (sectionId, oldProductKey). */
  productKey: string;
}

/**
 * A configured-but-never-counted product with no order-history link, deduped to one row per
 * product across all its sections. Mirrors the server's `CountReviewConfigNotLinkedProduct`.
 * The base fields name the FIRST assignment so every shared column/filter/action helper works
 * on it unchanged; `assignments` is what a fix must fan out over.
 */
export interface CountReviewConfigNotLinkedProduct extends CountReviewProduct {
  assignments: CountReviewAssignment[];
}

/**
 * One below-minimum product, aggregated across the sections it was counted in within a
 * group. Mirrors the server's `CountReviewBelowMinProduct` in `countReviewBuilder.ts`.
 */
export interface CountReviewBelowMinProduct {
  productKey: string;
  productName: string;
  groupId: string | null;
  groupName: string | null;
  /** Section names where the product was counted in this review scope. */
  sections: string[];
  /** Total counted across sections, in count units. */
  currentCount: number;
  countUnit: string;
  currentInStdUnit: number;
  stdUnit: string;
  /** The real minimum in count units; null for legacy flat-threshold entries. */
  minimum: number | null;
  /** How many count units to order at least (ceil(minimum − currentCount)); null for legacy entries. */
  toOrder: number | null;
  /**
   * The shortfall in PURCHASE units (order-history quantity info: one purchased unit =
   * stdAmount × stdMultiplier std units). Null when legacy entry or no live quantity info.
   */
  toOrderUnits: number | null;
  /** Total frozen value across sections. */
  value: number;
  hasRealMinimum: boolean;
  /** Same shape as CountReviewProduct.supplier — platform for order products, free-text name for customs. */
  supplier: { platform: string | null; name: string | null };
}

/**
 * The minimum status of one product that has a real minimum ("Standaard voorraad"),
 * emitted whether or not the minimum is met (the below-min list only carries the
 * shortfalls). Every number is a TOTAL across the sections the product was counted in.
 * Mirrors the server's `CountReviewProductMinimum`.
 */
export interface CountReviewProductMinimum {
  /** Base product key (variant rows are summed into one entry). */
  productKey: string;
  productName: string;
  groupId: string | null;
  /** The minimum in count units, as imported. */
  minimum: number;
  /** The unit the minimum is expressed in (may differ from a section's count unit). */
  countUnit: string;
  /** Total counted across sections, converted to `countUnit`. */
  currentCount: number;
  /** ceil(minimum − currentCount); 0 when the minimum is met. */
  toOrder: number;
  /** The same shortfall in PURCHASE units; null when there is no live quantity info. */
  toOrderUnits: number | null;
  belowMinimum: boolean;
}

export type NeedsReviewReasonKind = 'spike' | 'drop' | 'zeroed' | 'firstCount' | 'extraDigit' | 'swap';

export interface NeedsReviewReason {
  kind: NeedsReviewReasonKind;
  /** Interpolation values for `inventory.review.reason<Kind>` (already formatted). */
  params: Record<string, string | number>;
}

/**
 * One flagged product of the Needs review tab. Mirrors the server's `NeedsReviewRow`
 * (`server/domains/inventoryReview/needsReviewCore.ts`) — the rules run there, this page
 * only renders. `expectedCount` is deliberately named for what it means rather than how
 * it is derived today (`expectedSource: 'typical'` = the median of the previous counts).
 */
export interface NeedsReviewProduct {
  /** Base product key — variant rows are one product here. */
  productKey: string;
  productName: string;
  groupId: string | null;
  groupName: string | null;
  /** Names of the sections the product was counted in, in this selection. */
  sections: string[];
  /** Ids of those same sections (a count whose section was deleted contributes no id). */
  sectionIds: string[];
  countUnit: string;
  /** Total counted now, across sections, in count units. */
  currentCount: number;
  /** What the count was expected to be, in count units. */
  expectedCount: number;
  expectedSource: 'typical';
  /** The most recent previous count; null when that session didn't include the product. */
  previousCount: number | null;
  /** How many previous sessions carried this product. */
  sessionsCompared: number;
  ratio: number | null;
  /** € per count unit behind the impact. */
  unitPrice: number;
  /** Frozen value of the current count. */
  value: number;
  /** € size of the deviation — used for ranking only, never to gate a flag. */
  impact: number;
  reasons: NeedsReviewReason[];
  supplier: { platform: string | null; name: string | null };
}

export interface CountReviewResponse {
  selection: {
    groupId: string | null;
    sessionId: string | null;
    completedAt: string | null;
    /** The explicit "Compared against" count; null = the automatic previous count. Optional: older servers omit it. */
    baselineSessionId?: string | null;
    baselineCompletedAt?: string | null;
  };
  kpis: {
    totalValue: number;
    previousTotalValue: number | null;
    changeValue: number | null;
    productsCount: number;
    notLinkedCount: number;
    needsOrderingCount: number;
  };
  groups: {
    groupId: string | null;
    groupName: string | null;
    totalValue: number;
    share: number;
    sectionCount: number;
    productCount: number;
    belowMinCount: number;
    changeValue: number | null;
  }[];
  sections: {
    sectionId: string | null;
    sectionName: string;
    /** The saved count this section row aggregates (targets add/delete of product lines). */
    countId: string;
    groupId: string | null;
    groupName: string | null;
    totalValue: number;
    share: number;
    productCount: number;
    belowMinCount: number;
    lastCountedAt: string | null;
    /** vs the same section in the group's previous session; null when not counted there. */
    changeValue: number | null;
  }[];
  categoriesInventory: {
    categoryId: string | null;
    name: string;
    totalValue: number;
    share: number;
    productCount: number;
    belowMinCount: number;
  }[];
  categoriesProduct: {
    key: string;
    mainCategory: string | null;
    totalValue: number;
    share: number;
    productCount: number;
    belowMinCount: number;
    /** Per-subcategory breakdown (product's first subCategory; null = no subcategory). Share is of the grand total. */
    subCategories: {
      key: string | null;
      totalValue: number;
      share: number;
      productCount: number;
      belowMinCount: number;
    }[];
  }[];
  products: CountReviewProduct[];
  /**
   * Counted numbers that look like counting mistakes, most costly deviation first —
   * detected server-side (`server/domains/inventoryReview/needsReviewCore.ts`) against the
   * group's previous sessions, so this page and the Cloud Count app flag identically.
   */
  needsReview: NeedsReviewProduct[];
  /** One entry per below-minimum product, aggregated across sections within a group. */
  belowMinProducts: CountReviewBelowMinProduct[];
  /** One entry per counted product WITH a real minimum, met or not (see the type). */
  productMinimums: CountReviewProductMinimum[];
  /**
   * Configured products inventory can't value that no selected count covers (one row per
   * productKey). Kept out of `products` on purpose — they have no frozen quantity or value,
   * so they must not reach the other tabs, the KPIs, the export or the below-minimum logic.
   * Only the missing-price tab unions them in.
   */
  configMissingPrice: CountReviewProduct[];
  /**
   * SUPER ADMIN ONLY — `undefined` for every other user, because the server omits the field
   * rather than sending an empty array. That omission is the whole gate: presence of the field
   * is what renders the extra attention tab.
   *
   * Configured products with no order-history link that no selected count covers. `products`
   * is built from frozen count items only, so a custom product nobody counted (a zero count is
   * dropped at finalize) never reaches the "Not linked" tab — nor the link automation behind it,
   * which posts exactly the rows that tab shows. Kept out of `products` and every KPI for the
   * same reason `configMissingPrice` is.
   */
  configNotLinked?: CountReviewConfigNotLinkedProduct[];
  /**
   * "Compared against" options when exactly one group is in scope, newest first: [0] is the
   * automatic previous count (what an omitted baseline compares against — a legacy date bucket
   * has a null id), then every older saved session. Optional: older servers omit it.
   */
  baselineOptions?: { sessionId: string | null; completedAt: string; totalValue: number }[];
}

/**
 * One call returns everything the Count Review page needs for a viewing selection.
 * `groupId`/`sessionId` omitted (or null) = default view (latest count per group, all groups);
 * `baselineSessionId` omitted = changes measured against the automatic previous count.
 */
export async function fetchCountReview(
  restaurantId: string,
  selection: { groupId?: string | null; sessionId?: string | null; baselineSessionId?: string | null } = {}
): Promise<CountReviewResponse> {
  const params = new URLSearchParams();
  if (selection.groupId) params.set('groupId', selection.groupId);
  if (selection.sessionId) params.set('sessionId', selection.sessionId);
  if (selection.baselineSessionId) params.set('baselineSessionId', selection.baselineSessionId);
  const qs = params.toString();
  const res = await fetch(`/api/inventory/count-review/${restaurantId}${qs ? `?${qs}` : ''}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch inventory count review: ${res.status}`);
  return res.json();
}

/**
 * Fix a missing/zero price frozen inside a saved count (Count Review "Set price").
 * `priceExVat` is per the item's counted unit. For custom ingredients the server also
 * updates the ingredient's global price (ex VAT + VAT rate); order products only get
 * the frozen count patched — order history stays untouched.
 */
export async function setCountProductPrice(
  countId: string,
  payload: {
    productKey: string;
    source: InventoryProductSource;
    priceExVat: number;
    taxRate?: number;
    inventorySectionProductId?: string | null;
  }
): Promise<void> {
  rejectInventoryPracticeWrite(countId, payload);
  const res = await fetch(`/api/inventory/counts/${countId}/product-price`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to set product price: ${res.status}`);
}

/**
 * Durably set a product's inventory valuation price, restaurant-wide across sections.
 * Unlike `setCountProductPrice` (which patches ONE frozen count) this survives into every
 * future count: the server applies it as a fallback wherever the live order price is null
 * or 0, so a real invoice price always wins again later. Order history is never written to.
 * `priceExVat` is per the given count unit. Custom ingredients are routed server-side to
 * their own durable price on the ingredient row.
 */
export async function setInventoryPriceOverride(payload: {
  restaurantId: string;
  productKey: string;
  priceExVat: number;
  countUnit?: string;
  taxRate?: number;
  inventorySectionProductId?: string | null;
}): Promise<void> {
  rejectInventoryPracticeWrite(payload);
  const res = await fetch('/api/inventory/price-override', {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to set inventory price: ${res.status}`);
}

/** Remove a durable inventory price so the product falls back to its live order price. */
export async function deleteInventoryPriceOverride(restaurantId: string, productKey: string): Promise<void> {
  rejectInventoryPracticeWrite(restaurantId, productKey);
  const res = await fetch(
    `/api/inventory/price-override/${encodeURIComponent(productKey)}?restaurantId=${encodeURIComponent(restaurantId)}`,
    { method: 'DELETE', headers: await authHeaders() }
  );
  if (!res.ok) throw new Error(`Failed to remove inventory price: ${res.status}`);
}

/**
 * Set a product's minimum stock ("Standaard voorraad") — the same value the Excel importer
 * writes and the count review's below-minimum tab reads. Restaurant-wide per product: it is
 * a CROSS-SECTION total, not a per-section number. `minStockCount` is in the given
 * assignment's count unit; the server converts to the std unit with that unit's factor.
 */
export async function setInventoryProductMinimum(payload: {
  restaurantId: string;
  productKey: string;
  minStockCount: number;
  inventorySectionProductId?: string | null;
}): Promise<{ minStockCount: number; countUnit: string }> {
  rejectInventoryPracticeWrite(payload);
  const res = await fetch('/api/inventory/product-minimum', {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to set minimum stock: ${res.status}`);
  return res.json();
}

/** Clear a product's minimum stock (its group falls back to the flat 2 kg / 2 L / 2 pcs rule). */
export async function deleteInventoryProductMinimum(restaurantId: string, productKey: string): Promise<void> {
  rejectInventoryPracticeWrite(restaurantId, productKey);
  const res = await fetch(
    `/api/inventory/product-minimum/${encodeURIComponent(productKey)}?restaurantId=${encodeURIComponent(restaurantId)}`,
    { method: 'DELETE', headers: await authHeaders() }
  );
  if (!res.ok) throw new Error(`Failed to remove minimum stock: ${res.status}`);
}

// ---------- Excel importer (one-time structure + counts bootstrap) ----------

export type ImportMatchStatus = 'auto' | 'review' | 'unmatched';

export interface ImportMatchSuggestion {
  productKey: string;
  productName: string;
  supplier?: string;
  lastOrderDate?: string;
  last60Qty?: number;
  last60Unit?: string;
  unitPrice?: number;
  unitPriceUnit?: string;
  productPrice?: number;   // ex-VAT buy price per product (matches the Excel keyed price)
}

export interface ImportMatch {
  status: ImportMatchStatus;
  productKey?: string;
  matchedName?: string;
  confidence?: number;
  method?: 'exact' | 'ai';
  suggestions?: ImportMatchSuggestion[];
}

export interface ImportProductRow {
  rowIndex: number;
  name: string;
  category: string;
  counts: Record<string, number>;
  excelNetto: number | null;
  /** "Standaard voorraad" (minimum stock threshold) parsed from the sheet; null when absent. */
  minimum: number | null;
  /** Raw supplier ("LEVERANCIER") from the sheet, when the layout carries one; pass-through only. */
  supplier?: string;
  /** Sections assigned by sheet position, including products with blank or zero counts. */
  memberSections?: string[];
  totalCount: number;
  hasCount: boolean;
  match: ImportMatch;
  alreadyImported?: boolean;
}

export interface ImportPlan {
  sections: string[];
  categories: string[];
  products: ImportProductRow[];
  // Stock-take date parsed from the uploaded file's name ('YYYY-MM-DD'); the commit
  // books the count session on this date instead of the upload date. Null when the
  // file name carries no recognizable date.
  fileDate?: string | null;
  /** True for layouts without count columns (membership by sheet, e.g. Vader Kleinjan):
   *  the commit must never book a count session from this plan. */
  structureOnly?: boolean;
}

export interface ImportDryRunSummary {
  totalProducts: number;
  productsWithCount: number;
  autoMatched: number;
  toReview: number;
  unmatched: number;
  alreadyImported: number;
  sections: number;
  categories: number;
  /** Rows carrying a "Standaard voorraad" minimum (minimums-import mode). */
  withMinimum: number;
  /** Rows carrying a "Leverancier" supplier (suppliers-import mode). */
  withSupplier: number;
}

/** Rebuild-sections mode: what the commit would do to one section's product list. */
export interface ImportRebuildPreviewRow {
  section: string;
  current: number;   // assignments the section holds today
  keep: number;      // of those, still counted by the file here
  remove: number;    // current - keep (upper bound; key matches can only lower it)
  add: number;       // counted by the file here but not assigned yet
}

export interface ImportDryRunResponse {
  plan: ImportPlan;
  summary: ImportDryRunSummary;
  drinksStructureExists: boolean;
  /** Only returned when the dry-run was asked for in rebuild-sections mode. */
  rebuildPreview?: ImportRebuildPreviewRow[];
}

export type ImportResolution =
  | { action: 'bind'; productKeys: string[] }   // one or more; first carries the count
  | { action: 'createCustom'; price: number }
  | { action: 'skip' };

export interface ImportCommitProduct {
  rowIndex: number;
  name: string;
  category: string;
  counts: Record<string, number>;
  excelNetto: number | null;
  /** "Standaard voorraad" minimum from the sheet; used in minimums-import mode and stored inline for structure-only rows. */
  minimum?: number | null;
  /** Raw supplier ("LEVERANCIER") from the sheet; seeds the platform of a created custom. */
  supplier?: string;
  /** Sections assigned by sheet position, independently of counts. */
  memberSections?: string[];
  resolution: ImportResolution;
}

export interface ImportCommitSummary {
  sectionsCreated: number;
  categoriesCreated: number;
  assignmentsCreated: number;
  assignmentsSkipped: number;
  customIngredientsCreated: number;
  /** How many reused customs had their supplier backfilled from the sheet. */
  customSuppliersBackfilled?: number;
  countsApplied: number;
  skipped: number;
  /** Per-product minimums upserted (minimums mode, or inline from a structure-only file). */
  minimumsApplied?: number;
  /** Rebuild mode only: assignments moved into their sheet category. */
  rebuildPromoted?: number;
  /** Rebuild mode only: assignments removed from sections the file doesn't count them in. */
  rebuildRemoved?: number;
  /** Rebuild mode only: the now-empty "From Other Sections" category was dropped. */
  catchAllCategoryDeleted?: boolean;
}

/**
 * Upload the Excel and get a parsed + matched plan back (no writes).
 * includeZeroCount = catalogue-fill mode: keep zero-count rows so the whole
 * catalogue can be matched and assigned to every section.
 * rebuildSections = also return the per-section keep/add/remove preview.
 * completeCatalogue = top-up mode for a file with no counts: keeps every row so products
 * missing from the structure can be matched and added.
 */
export async function importInventoryDryRun(
  restaurantId: string,
  file: File,
  includeZeroCount = false,
  rebuildSections = false,
  suppliersOnly = false,
  completeCatalogue = false
): Promise<ImportDryRunResponse> {
  rejectInventoryPracticeWrite(restaurantId, file, includeZeroCount, rebuildSections, suppliersOnly, completeCatalogue);
  const token = await getAccessToken();
  const formData = new FormData();
  formData.append('restaurantId', restaurantId);
  formData.append('file', file);
  if (includeZeroCount) formData.append('includeZeroCount', 'true');
  if (rebuildSections) formData.append('rebuildSections', 'true');
  // Suppliers mode needs no matching decisions from the user — skips the paid AI pass.
  if (suppliersOnly) formData.append('suppliersOnly', 'true');
  if (completeCatalogue) formData.append('completeCatalogue', 'true');
  const res = await fetch('/api/inventory/import/dry-run', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) throw new Error(`Inventory import dry-run failed: ${res.status}`);
  return res.json();
}

/**
 * Commit a reviewed plan: create structure + optional count session.
 * applyRebuildSections = rebuild-sections mode: normal per-section import, then
 * remove every assignment the file does not count in that section. Assignments
 * only — saved counts are untouched.
 * applySuppliers = suppliers mode: write the sheet's "Leverancier" onto the custom
 * ingredients the rows resolve to, and nothing else.
 * applyComplete = complete mode: add every product the file has that is not assigned yet
 * (to every section the plan names) and re-state every row's minimum. Purely additive —
 * nothing is removed or moved, and the server refuses to book a count in this mode.
 */
export async function importInventoryCommit(
  restaurantId: string,
  categories: string[],
  products: ImportCommitProduct[],
  applyCounts: boolean,
  sections: string[] = [],
  countedAt: string | null = null,
  applyMinimums = false,
  applyRebuildSections = false,
  applySuppliers = false,
  applyComplete = false
): Promise<ImportCommitSummary> {
  rejectInventoryPracticeWrite(restaurantId, categories, products, applyCounts, sections, countedAt, applyMinimums, applyRebuildSections, applySuppliers, applyComplete);
  const res = await fetch('/api/inventory/import/commit', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, categories, products, applyCounts, sections, countedAt, applyMinimums, applyRebuildSections, applySuppliers, applyComplete }),
  });
  if (res.status === 409) throw new Error('active_session');
  if (!res.ok) throw new Error(`Inventory import commit failed: ${res.status}`);
  const data = await res.json();
  return data.summary as ImportCommitSummary;
}

// ---------- "Link products" automation ----------
//
// Bulk re-link of the "Not linked" rows shown on the Inventory page. The dry-run posts
// the visible rows and gets a matched, reviewable plan back (order-history candidates
// only); the commit applies the reviewed choices through the same server code path as
// the single-row Link action.

/** One not-linked row as posted to the dry-run. */
export interface LinkTargetInput {
  rowIndex: number;
  sectionId: string;
  productKey: string;
  productName: string;
  sectionName: string | null;
  inventoryCategoryId: string | null;
  inventoryCategoryName: string | null;
  count: number | null;
  countUnit: string | null;
  /** The row's own frozen price per std unit (kg/L/pcs) — a hint for picking a match with a
   *  similar price. Display-only; the server ignores it. */
  price?: number | null;
  priceUnit?: string | null;
  /**
   * The OTHER sections holding this same product, when the caller deduped one display row out
   * of several assignments (the super-admin "Not linked (uncounted)" tab). Client-side only —
   * the server never reads it, and the dry-run matches the row once. The modal expands it at
   * COMMIT time into one re-link per section, because re-linking is per section (M40) while
   * the AI match and the human review are per product.
   */
  additionalTargets?: { sectionId: string; productKey: string; inventoryCategoryId: string | null }[];
}

/** A dry-run row: the posted target plus its match. */
export interface LinkPlanRow extends LinkTargetInput {
  name: string;
  category: string;
  match: ImportMatch;
}

export interface LinkDryRunSummary {
  total: number;
  autoMatched: number;
  toReview: number;
  unmatched: number;
  /** Order-history products available as candidates — 0 means nothing can ever match. */
  availableProducts: number;
}

export interface LinkDryRunResponse {
  products: LinkPlanRow[];
  summary: LinkDryRunSummary;
}

/** What the user decided for one row. 'skip' rows are simply not sent to the commit. */
export type LinkResolution =
  | { action: 'bind'; productKey: string }
  | { action: 'skip' };

export interface LinkCommitProduct {
  rowIndex: number;
  sectionId: string;
  oldProductKey: string;
  productKey: string;
  inventoryCategoryId: string | null;
}

export type LinkCommitStatus = 'updated' | 'already_in_section' | 'not_found' | 'target_not_found' | 'error';

export interface LinkCommitSummary {
  requested: number;
  linked: number;
  alreadyInSection: number;
  notFound: number;
  targetNotFound: number;
  failed: number;
}

export interface LinkCommitResponse {
  summary: LinkCommitSummary;
  results: Array<{ rowIndex: number; status: LinkCommitStatus }>;
}

/** Match the given not-linked rows against order history. No writes. */
export async function linkProductsDryRun(
  restaurantId: string,
  products: LinkTargetInput[]
): Promise<LinkDryRunResponse> {
  rejectInventoryPracticeWrite(restaurantId, products);
  const res = await fetch('/api/inventory/link/dry-run', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, products }),
  });
  if (!res.ok) throw new Error(`Link products dry-run failed: ${res.status}`);
  return res.json();
}

/** Apply the reviewed links. Never throws per row — check `results` for partial failures. */
export async function linkProductsCommit(
  restaurantId: string,
  products: LinkCommitProduct[]
): Promise<LinkCommitResponse> {
  rejectInventoryPracticeWrite(restaurantId, products);
  const res = await fetch('/api/inventory/link/commit', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, products }),
  });
  if (!res.ok) throw new Error(`Link products commit failed: ${res.status}`);
  return res.json();
}
